import React, { useEffect, useRef, useState } from 'react';
import { Upload, Download, Info } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '../../../ui/alert';
import { Button } from '../../../ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../ui/dialog';
import { downloadInvitationTemplateCsv, parseInvitationCsvText } from '../surveyInvitationCsv';

const MAX_BYTES = 2 * 1024 * 1024;

const readFileAsText = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsText(file);
  });

const ImportEmployeesDialog = ({ open, onOpenChange, campaignName, facilities, loading, onCommit }) => {
  const [parseResult, setParseResult] = useState(null);
  const [fileError, setFileError] = useState(null);
  const fileInputRef = useRef(null);

  const reset = () => {
    setParseResult(null);
    setFileError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  useEffect(() => {
    if (!open) reset();
  }, [open]);

  const handleFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    setFileError(null);
    setParseResult(null);
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setFileError('File is too large (max 2 MB).');
      return;
    }
    try {
      const text = await readFileAsText(file);
      setParseResult(parseInvitationCsvText(text));
    } catch (e) {
      setFileError(e?.message || 'Could not read file');
    }
  };

  const handleImport = async () => {
    if (!parseResult?.invitations?.length) return;
    const ok = await onCommit(parseResult.invitations);
    if (ok) onOpenChange(false);
  };

  const handleDialogTemplate = () => downloadInvitationTemplateCsv(campaignName, facilities);

  const validCount = parseResult?.invitations?.length || 0;
  const skippedCount = parseResult?.skipped?.length || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-xl max-h-[90vh] overflow-y-auto' onClose={() => onOpenChange(false)}>
        <DialogHeader>
          <DialogTitle>Import employees</DialogTitle>
          <DialogDescription>Upload a CSV with the same columns as the template: name, email, and office location.</DialogDescription>
        </DialogHeader>

        <DialogBody className='space-y-4'>
          <label
            htmlFor='survey-invitation-csv-input'
            className='relative block border-2 border-dashed border-input rounded-xl p-8 text-center cursor-pointer hover:border-primary transition-colors'>
            <input
              ref={fileInputRef}
              id='survey-invitation-csv-input'
              type='file'
              accept='.csv,text/csv'
              className='sr-only'
              onChange={handleFile}
            />
            <div className='w-12 h-12 mx-auto rounded-full bg-primary/10 flex items-center justify-center text-accent mb-3'>
              <Upload size={20} strokeWidth={1.5} />
            </div>
            <div className='font-medium mb-1'>Drop your CSV here, or click to browse</div>
            <div className='text-xs text-muted-foreground'>Up to 5,000 rows · max 2 MB</div>
          </label>

          {fileError ? (
            <Alert variant='destructive'>
              <AlertTitle>Upload</AlertTitle>
              <AlertDescription>{fileError}</AlertDescription>
            </Alert>
          ) : null}

          <div className='flex items-center justify-between text-xs text-muted-foreground'>
            <span>Need a template?</span>
            <Button type='button' variant='link' size='sm' className='h-auto p-0' onClick={handleDialogTemplate}>
              <Download size={12} strokeWidth={1.5} className='mr-1' />
              Download CSV template
            </Button>
          </div>

          <Alert>
            <Info size={14} strokeWidth={1.5} />
            <AlertTitle>Required columns</AlertTitle>
            <AlertDescription>
              <code className='font-mono text-xs'>name,email,location</code> — location must match an office name on this campaign (see
              list below). Quote the location cell if it contains a comma.
            </AlertDescription>
          </Alert>

          {facilities?.length ? (
            <div className='text-xs text-muted-foreground rounded-lg border border-border p-3 space-y-1'>
              <div className='font-medium text-foreground'>Office names on this campaign</div>
              <ul className='text-[11px] space-y-0.5 max-h-28 overflow-y-auto'>
                {facilities.map((f) => (
                  <li key={f.id || f.name}>{f.name || '—'}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {parseResult ? (
            <div className='rounded-lg border border-border p-3 text-sm space-y-2'>
              <div>
                <span className='font-medium text-foreground'>{validCount}</span> row{validCount === 1 ? '' : 's'} ready to import
              </div>
              {skippedCount > 0 ? (
                <div className='text-xs text-warning'>
                  {skippedCount} row{skippedCount === 1 ? '' : 's'} skipped
                  <ul className='mt-1 list-disc pl-4 text-muted-foreground max-h-24 overflow-y-auto'>
                    {parseResult.skipped.slice(0, 12).map((item, index) => (
                      <li key={`${item.line}-${index}`}>
                        Line {item.line}: {item.reason}
                      </li>
                    ))}
                    {parseResult.skipped.length > 12 ? <li>…and more</li> : null}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : null}
        </DialogBody>

        <DialogFooter>
          <Button type='button' variant='ghost' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type='button' onClick={handleImport} disabled={!validCount} loading={Boolean(loading)}>
            Import{validCount ? ` ${validCount}` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportEmployeesDialog;
